import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileText, ChevronDown, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { TemplateModal } from "./TemplateModal";
import { format } from "date-fns";
import { toast } from "sonner";

interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  body_html: string;
  description: string | null;
  variables: string[];
}

interface InquiryData {
  first_name: string;
  last_name: string;
  email: string;
  wedding_date: string | null;
  venue: string | null;
  services: string[] | null;
  budget: string | null;
  selected_package: string | null;
}

interface TemplateSelectorProps {
  inquiry: InquiryData | null;
  onSelectTemplate: (subject: string, body: string) => void;
}

const categoryLabels: Record<string, string> = {
  availability: "Availability",
  pricing: "Pricing",
  follow_up: "Follow-up",
  general: "General",
};

export function TemplateSelector({ inquiry, onSelectTemplate }: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const replaceVariables = (text: string): string => {
    if (!inquiry) return text;

    let result = text;

    result = result.replace(/\{\{first_name\}\}/g, inquiry.first_name || "there");

    if (inquiry.wedding_date) {
      const formattedDate = format(new Date(inquiry.wedding_date), "MMMM d, yyyy");
      result = result.replace(/\{\{wedding_date\}\}/g, formattedDate);
      result = result.replace(/\{\{wedding_date_mention\}\}/g, ` on ${formattedDate}`);
    } else {
      result = result.replace(/\{\{wedding_date\}\}/g, "your special day");
      result = result.replace(/\{\{wedding_date_mention\}\}/g, "");
    }

    if (inquiry.venue) {
      result = result.replace(/\{\{venue\}\}/g, inquiry.venue);
      result = result.replace(/\{\{venue_mention\}\}/g, ` at ${inquiry.venue}`);
    } else {
      result = result.replace(/\{\{venue\}\}/g, "your venue");
      result = result.replace(/\{\{venue_mention\}\}/g, "");
    }

    if (inquiry.services && inquiry.services.length > 0) {
      result = result.replace(/\{\{services\}\}/g, inquiry.services.join(", "));
    } else {
      result = result.replace(/\{\{services\}\}/g, "our services");
    }

    result = result.replace(/\{\{package_details\}\}/g, 
      inquiry.selected_package 
        ? `<em>${inquiry.selected_package} - [Add pricing and details here]</em>`
        : "<em>[Select and describe the recommended package here]</em>"
    );

    return result;
  };

  const handleSelectTemplate = (template: EmailTemplate) => {
    const processedSubject = replaceVariables(template.subject);
    const processedBody = replaceVariables(template.body_html);
    onSelectTemplate(processedSubject, processedBody);
  };

  const handleEditClick = (e: React.MouseEvent, template: EmailTemplate) => {
    e.stopPropagation();
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, template: EmailTemplate) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("email_templates")
        .update({ is_active: false })
        .eq("id", templateToDelete.id);

      if (error) throw error;

      toast.success("Template deleted successfully");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setModalOpen(open);
    if (!open) {
      setEditingTemplate(null);
    }
  };

  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || "general";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, EmailTemplate[]>);

  const categoryOrder = ["availability", "pricing", "follow_up", "general"];
  const sortedCategories = Object.keys(groupedTemplates).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled>
        <FileText className="h-4 w-4 mr-2" />
        Loading...
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <FileText className="h-4 w-4 mr-2" />
            Use Template
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          {sortedCategories.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              No templates yet
            </div>
          ) : (
            sortedCategories.map((category, idx) => (
              <div key={category}>
                {idx > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="flex items-center gap-2">
                  {categoryLabels[category] || category}
                  <Badge variant="secondary" className="text-xs">
                    {groupedTemplates[category].length}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuGroup>
                  {groupedTemplates[category].map((template) => (
                    <DropdownMenuItem
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="flex items-start justify-between gap-2 cursor-pointer group"
                    >
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <span className="font-medium truncate">{template.name}</span>
                        {template.description && (
                          <span className="text-xs text-muted-foreground truncate">
                            {template.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button
                          type="button"
                          onClick={(e) => handleEditClick(e, template)}
                          className="p-1.5 hover:bg-accent rounded"
                          title="Edit template"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteClick(e, template)}
                          className="p-1.5 hover:bg-destructive/10 rounded"
                          title="Delete template"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </button>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </div>
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleCreateNew}
            className="cursor-pointer"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <TemplateModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        onSuccess={fetchTemplates}
        template={editingTemplate}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
